
import PrintPositionsManagerV2 from '../../../print-positions-manager-v2/print-positions-manager-v2.vue';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import Vue from 'vue';
import { mapActions, mapState } from 'pinia';
import { UnavailableProductPrintPosition } from '../../typings/product';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';

export default Vue.extend({
  name: 'PrintPositionsModal',
  components: {
    PrintPositionsManagerV2,
  },
  props: {
    sapCustomerId: {
      type: String,
      required: true,
    },
    editId: {
      type: String,
      required: false,
      default: '',
    },
    tok: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState(useProductStore, [
      'selectedVariant',
      'unavailablePrintPositions',
      'isSmallDetailsTechnique',
      'isLaserConverter',
      'isDebossingTechnique',
      'isSublimationTechnique',
      'getProductPrintPosition',
      'isTraditionalTechnique',
    ]),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useShapeStore, ['shapes', 'positionShapes']),
  },
  methods: {
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useShapeStore, [
      'applyEffectIfNeeded',
      'revertAppliedEffect',
      'updateShapePosition',
      'resetSmallDetailsResult',
      'updateShapeImages',
    ]),
    ...mapActions(useMirrorStore, ['updateMirrorShape']),
    ...mapActions(useColorsManagementStore, ['getAvailablePmsColors']),
    ...mapActions(useProductStore, ['printPositionArea']),

    handleLoadingNotificationsAndChecks(loading: any) {
      this.$emit('loadingNotificationsAndChecks', loading);
    },

    async onEditPrintPosition(
      positions: UnavailableProductPrintPosition[],
      newPositionId: string,
      oldPositionId: string
    ) {
      const technique = this.unavailablePrintPositions.find(
        (pp) => pp.positionId === newPositionId
      )?.technique;

      if (technique) {
        const isSmallDetailsTech = this.isSmallDetailsTechnique(
          newPositionId,
          technique.printTechniqueSAPCode
        );

        await this.getAvailablePmsColors();

        if (newPositionId) {
          const positionShapes = this.positionShapes(newPositionId);

          const isTraditionalTechnique = this.isTraditionalTechnique(
            newPositionId,
            technique.printTechniqueSAPCode
          );

          for (const shape of positionShapes) {
            this.resetSmallDetailsResult(shape.id, isSmallDetailsTech);

            await this.updateShapeImages(shape.id, isTraditionalTechnique);

            this.revertAppliedEffect(shape);

            await this.applyEffectIfNeeded(newPositionId, technique.id, shape);

            const scaledArea = this.printPositionArea(newPositionId, true);
            const printPosition = this.getProductPrintPosition(newPositionId);

            if (printPosition && scaledArea) {
              this.updateShapePosition(
                shape,
                scaledArea,
                printPosition.printArea,
                newPositionId != oldPositionId
              );
            }

            this.updateMirrorShape(shape);
          }
        }

        this.runValidators();
        this.$emit('loadingNotificationsAndChecks', 'end');

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      }
    },
  },
});
